import { Box, Link, Paper, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import { useTheme } from "../context/ThemeContext";
import { percentage } from "../shared/percentage";
import { ProgressBar } from "./CostProgressBar";
import { PartyAvatar } from "./PartyAvatar";
import {
  signedTone,
  TABULAR_NUMERALS,
  Tone,
  toneColors,
} from "./general/dataAtoms/shared";
import { VsLitigationLine } from "./analytics/VsLitigation";
import { TonalBadge } from "./general/TonalBadge";

export type PartyOverview = {
  id: string;
  name: string;
  client?: boolean;
  assetLimit: number | "unlimited";
  costs: {
    incurred: number;
    estimated: number;
  };
  winChance?: number;
  settled?: boolean;
  netResult?: number;
  agreements?: {
    lawFirmCfa?: boolean;
    barristerCfa?: boolean;
  };
  // where the case has settlement-adjusted results: every figure keeps a
  // line under it in both views, so switching moves nothing; in the
  // settlement-adjusted view the line says how the net result stands
  // against litigation, and how likely the party settles with everyone
  withSettlements?: {
    netResult?: VsLitigationLine;
    settling?: number;
  };
};

/**
 * One thing to know about a party, under its name and beside the way to the
 * page it comes from.
 */
const Linked = ({
  label,
  link,
  href,
  children,
}: {
  label: string;
  link: string;
  href: string;
  children: ReactNode;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "baseline", justifyContent: "space-between", mb: 0.25 }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Link
        component={RouterLink}
        to={href}
        variant="caption"
        underline="hover"
        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
      >
        {link}
      </Link>
    </Stack>
    {children}
  </Box>
);

export const PartyOverviewCard = ({
  name,
  client,
  assetLimit,
  costs: { incurred, estimated },
  winChance,
  settled,
  netResult,
  agreements,
  withSettlements,
}: PartyOverview) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation();
  const tones = toneColors(theme);
  const totalCosts = estimated + incurred;

  const figure = (value: string, tone: Tone) => (
    <Typography
      variant="h5"
      component="p"
      sx={{
        fontWeight: 700,
        letterSpacing: "-.015em",
        color: tones[tone],
        ...TABULAR_NUMERALS,
      }}
    >
      {value}
    </Typography>
  );
  // a line under a figure, empty where it has nothing to say
  const line = (children?: ReactNode, tone?: Tone) =>
    withSettlements && (
      <Typography
        variant="caption"
        component="p"
        sx={{
          fontWeight: 600,
          color: tone ? tones[tone] : "text.secondary",
          ...TABULAR_NUMERALS,
        }}
      >
        {children || "\u00a0"}
      </Typography>
    );
  const settlingLine = line(
    withSettlements?.settling !== undefined && (
      <Link
        component={RouterLink}
        to="configuration/settlement"
        underline="hover"
        color="inherit"
      >
        {t("PartyOverviewCard.settlingChance", {
          chance: percentage.format(withSettlements.settling),
        })}
      </Link>
    ),
  );
  const agreement = (label: string) => (
    <Link
      component={RouterLink}
      to="configuration/agreements"
      underline="none"
      sx={{
        px: 1,
        py: 0.25,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.25,
        typography: "caption",
        fontWeight: 600,
        color: "text.secondary",
        whiteSpace: "nowrap",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {label}
    </Link>
  );

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden", userSelect: "none" }}
    >
      <Stack
        direction="row"
        spacing={1.75}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap", px: 2.5, py: 1.75 }}
      >
        <PartyAvatar client={client} />
        <Box sx={{ flex: 1, minWidth: theme.spacing(22) }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Link
              component={RouterLink}
              to="configuration/parties"
              underline="hover"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {name}
            </Link>
            {client && (
              <TonalBadge tone={theme.palette.primary}>
                {t("case.parties.clientBadge")}
              </TonalBadge>
            )}
          </Stack>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
          >
            {assetLimit === "unlimited"
              ? t("PartyOverviewCard.assets.unlimited")
              : t("PartyOverviewCard.assets.value", {
                  value: currency.format(assetLimit),
                })}
          </Typography>
        </Box>
        {(agreements?.lawFirmCfa || agreements?.barristerCfa) && (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {agreements.lawFirmCfa &&
              agreement(t("PartyOverviewCard.agreements.lawFirmCfa"))}
            {agreements.barristerCfa &&
              agreement(t("PartyOverviewCard.agreements.barristerCfa"))}
          </Stack>
        )}
      </Stack>

      {(totalCosts > 0 ||
        winChance !== undefined ||
        settled ||
        netResult !== undefined) && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${theme.spacing(24)}, 1fr))`,
            gap: theme.spacing(2.25, 3.5),
            px: 2.5,
            pt: 2,
            pb: 2.25,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {totalCosts > 0 && (
            <Linked
              label={t("PartyOverviewCard.costProgress")}
              link={t("case.details.section.configuration.costs")}
              href="configuration/costs"
            >
              <ProgressBar incurred={incurred} total={totalCosts} />
            </Linked>
          )}
          {settled ? (
            <Linked
              label={t("PartyOverviewCard.status")}
              link={t("PartyOverviewCard.tooltip.status")}
              href="configuration/settlement"
            >
              {figure(t("PartyOverviewCard.settled"), "favourable")}
              {line()}
            </Linked>
          ) : (
            winChance !== undefined && (
              <Linked
                label={t("PartyOverviewCard.winChance")}
                link={t("PartyOverviewCard.tooltip.winChance")}
                href="analytics/outcomes"
              >
                {figure(
                  percentage.format(winChance),
                  // a party more likely to lose than to win is at a disadvantage
                  winChance < 0.5 ? "adverse" : "favourable",
                )}
                {settlingLine}
              </Linked>
            )
          )}
          {netResult !== undefined && (
            <Linked
              label={t("PartyOverviewCard.netResult")}
              link={t("PartyOverviewCard.tooltip.netResult")}
              href="analytics/netResults"
            >
              {figure(currency.format(netResult), signedTone(netResult))}
              {line(
                withSettlements?.netResult?.text,
                withSettlements?.netResult?.tone,
              )}
            </Linked>
          )}
        </Box>
      )}
    </Paper>
  );
};
